import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Building2, Save, Image } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const FIELDS = [
  { key: 'name', label: 'Company Name', placeholder: 'Company name' },
  { key: 'phone', label: 'Phone', placeholder: '+971...' },
  { key: 'email', label: 'Email', placeholder: 'info@company.com' },
  { key: 'address_line1', label: 'Address Line 1', placeholder: 'Street / building' },
  { key: 'address_line2', label: 'Address Line 2', placeholder: 'Area (optional)' },
  { key: 'city', label: 'City', placeholder: 'Dubai' },
  { key: 'country', label: 'Country', placeholder: 'UAE' },
];

export const CompanyProfileEditor = () => {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    axios.get(`${API_BASE_URL}/company/profile`, { headers: authHeaders() })
      .then((res) => setProfile(res.data))
      .catch(() => {});
  }, []);

  if (!profile) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_BASE_URL}/company/profile`, profile, { headers: authHeaders() });
      toast.success('Company profile updated — packing slips & invoices will use the new details');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update company profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be under 5MB');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_BASE_URL}/company/profile/logo`, formData, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setProfile((p) => ({ ...p, logo: res.data.logo_url }));
      toast.success('Logo updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-wt-paper border border-wt-border rounded-xl p-6 lg:col-span-2"
      data-testid="company-profile-section"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
          <Building2 className="h-6 w-6 text-amber-400" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-wt-text-primary">Company Profile</h2>
          <p className="text-sm text-wt-text-secondary">
            Shown on packing slips, invoices and customer portals
          </p>
        </div>
      </div>

      <div className="flex items-center gap-4 mb-6">
        {profile.logo ? (
          <img
            src={profile.logo.startsWith('http') ? profile.logo : process.env.REACT_APP_BACKEND_URL + profile.logo}
            alt="Company logo"
            className="w-16 h-16 rounded-lg object-contain border border-wt-border bg-white/5"
            data-testid="company-logo-preview"
          />
        ) : (
          <div className="w-16 h-16 rounded-lg border-2 border-dashed border-wt-border flex items-center justify-center text-wt-text-muted text-xs">
            No logo
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
        <Button
          type="button"
          variant="outline"
          disabled={uploading}
          className="border-wt-border text-wt-text-primary hover:bg-wt-subtle"
          onClick={() => fileRef.current?.click()}
          data-testid="upload-company-logo-btn"
        >
          <Image className="h-4 w-4 mr-2" />
          {uploading ? 'Uploading...' : 'Upload Logo'}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {FIELDS.map((f) => (
          <div key={f.key} className={f.key === 'name' ? 'md:col-span-2' : ''}>
            <Label className="text-wt-text-primary">{f.label}</Label>
            <Input
              value={profile[f.key] || ''}
              placeholder={f.placeholder}
              onChange={(e) => setProfile({ ...profile, [f.key]: e.target.value })}
              className="bg-wt-bg border-wt-border text-wt-text-primary mt-2"
              data-testid={`company-profile-${f.key}-input`}
            />
          </div>
        ))}
      </div>

      <Button
        onClick={handleSave}
        disabled={saving}
        className="mt-6 bg-amber-600 hover:bg-amber-700 text-white"
        data-testid="save-company-profile-btn"
      >
        <Save className="h-4 w-4 mr-2" />
        {saving ? 'Saving...' : 'Save Company Profile'}
      </Button>
    </motion.div>
  );
};

export default CompanyProfileEditor;
